import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';

const CareerSupport: React.FC = () => {
  const [activeStep, setActiveStep] = useState<number | null>(null);
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);

  const services = [
    {
      title: "Career Counseling",
      description: "Professional guidance to help you identify and achieve your career goals in Japan",
      icon: "🎯",
      link: "/career/counseling",
      color: "from-blue-500 to-indigo-600",
      features: [
        "Career path assessment",
        "Industry insights",
        "Skill gap analysis",
        "Goal setting"
      ],
      bgPattern: "radial-gradient(circle at 10% 20%, rgb(111, 123, 247) 0%, rgb(72, 149, 239) 100.2%)"
    },
    {
      title: "Job Search Support",
      description: "Comprehensive assistance in finding and applying for jobs in Japan",
      icon: "💼",
      link: "/career/jobs",
      color: "from-purple-500 to-pink-600",
      features: [
        "Job market research",
        "Application preparation",
        "Interview coaching",
        "Resume building"
      ],
      bgPattern: "radial-gradient(circle at 10% 20%, rgb(186, 116, 247) 0%, rgb(239, 72, 172) 100.2%)"
    },
    {
      title: "Professional Development",
      description: "Enhance your skills and qualifications for the Japanese job market",
      icon: "📈",
      link: "/career/development",
      color: "from-orange-500 to-red-600",
      features: [
        "Skill workshops",
        "Certification guidance",
        "Networking events",
        "Industry training"
      ],
      bgPattern: "radial-gradient(circle at 10% 20%, rgb(255, 126, 95) 0%, rgb(254, 180, 123) 100.2%)"
    }
  ];

  return (
    <div className="min-h-screen pt-24 relative overflow-hidden bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-gray-900 via-gray-800 to-gray-900 perspective-1000">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden z-0">
        {Array.from({ length: 50 }).map((_, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full mix-blend-screen filter blur-xl"
            style={{
              width: 100 + Math.random() * 300,
              height: 100 + Math.random() * 300,
              background: i % 3 === 0 
                ? 'rgba(59, 130, 246, 0.15)' 
                : i % 3 === 1 
                  ? 'rgba(139, 92, 246, 0.15)' 
                  : 'rgba(236, 72, 153, 0.15)',
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`
            }}
            animate={{
              x: [0, Math.random() * 100 - 50],
              y: [0, Math.random() * 100 - 50],
              scale: [1, 1.2, 1],
              rotate: [0, 360],
            }}
            transition={{
              duration: 20 + Math.random() * 10,
              repeat: Infinity,
              repeatType: 'reverse',
              ease: 'easeInOut',
              delay: Math.random() * 5
            }}
          />
        ))}
      </div>

      <div className="container mx-auto px-4 relative z-20">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          className="text-center mb-24"
        >
          <motion.div 
            className="inline-block mb-6"
            animate={{ 
              y: [0, -10, 0],
              rotateX: [0, 10, 0],
              rotateY: [0, 15, 0]
            }}
            transition={{ 
              duration: 6,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            <h2 className="text-6xl md:text-7xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 drop-shadow-[0_0_15px_rgba(191,219,254,0.3)]">
              Career Support
            </h2>
          </motion.div>
          <motion.div
            className="h-1 w-24 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 mx-auto mb-6 rounded-full shadow-[0_0_15px_rgba(191,219,254,0.5)]"
            initial={{ width: 0 }}
            whileInView={{ width: 96 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.5 }}
          />
          <motion.p 
            className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto font-medium"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.7 }}
          >
            Comprehensive career guidance and support to help you succeed in the Japanese job market.
          </motion.p>
        </motion.div>

        {/* Services Timeline */}
        <div className="relative max-w-5xl mx-auto">
          {/* Timeline line */}
          <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-blue-500/50 via-purple-500/50 to-pink-500/50 z-10 shadow-[0_0_15px_rgba(191,219,254,0.3)]" />

          {services.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              className={`relative mb-32 last:mb-0 ${
                index % 2 === 0 ? 'md:pr-1/2 md:pl-8' : 'md:pl-1/2 md:pr-8'
              }`}
              onMouseEnter={() => setHoveredCard(index)}
              onMouseLeave={() => setHoveredCard(null)}
            >
              {/* Timeline dot */}
              <motion.div
                className="absolute left-1/2 -translate-x-1/2 w-12 h-12 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center shadow-[0_0_15px_rgba(191,219,254,0.5)] z-30"
                whileHover={{ scale: 1.2, rotate: 360 }}
                animate={hoveredCard === index ? {
                  scale: [1, 1.2, 1],
                  rotate: [0, 360],
                } : {}}
                transition={{ duration: 0.8 }}
              >
                <span className="text-3xl">{service.icon}</span>
              </motion.div>

              {/* Content */}
              <motion.div
                className={`relative rounded-3xl z-20 transform-gpu ${
                  index % 2 === 0 ? 'md:mr-8' : 'md:ml-8'
                }`}
                animate={hoveredCard === index ? {
                  rotateX: [0, 5],
                  rotateY: index % 2 === 0 ? [0, 5] : [0, -5],
                  z: 20,
                } : {
                  rotateX: 0,
                  rotateY: 0,
                  z: 0,
                }}
                transition={{ duration: 0.4 }}
                style={{
                  transformStyle: 'preserve-3d',
                }}
              >
                <motion.div 
                  className="absolute inset-0 rounded-3xl bg-gradient-to-r from-blue-500/20 to-purple-600/20 backdrop-blur-xl transform -z-10"
                  animate={hoveredCard === index ? {
                    opacity: 1,
                    scale: 1.02,
                  } : {
                    opacity: 0.5,
                    scale: 1,
                  }}
                />
                <div className="relative p-8 rounded-3xl bg-white/10 backdrop-blur-xl shadow-[0_0_25px_rgba(191,219,254,0.2)] border border-white/20">
                  <h3 className="text-2xl font-bold mb-4 text-white">{service.title}</h3>
                  <p className="text-gray-300 mb-6">{service.description}</p>

                  {/* Features */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    {service.features.map((feature, i) => (
                      <motion.div
                        key={i}
                        className="flex items-center text-gray-300"
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.1 }}
                      >
                        <motion.div
                          className="w-2 h-2 rounded-full bg-gradient-to-r from-blue-400 to-purple-400 mr-3 shadow-[0_0_10px_rgba(191,219,254,0.5)]"
                          animate={{
                            scale: [1, 1.5, 1],
                            opacity: [0.5, 1, 0.5]
                          }}
                          transition={{
                            duration: 2,
                            repeat: Infinity,
                            delay: i * 0.2
                          }}
                        />
                        <span>{feature}</span>
                      </motion.div>
                    ))}
                  </div>

                  {/* <Link to={service.link}>
                    <motion.div
                      className="inline-flex items-center text-blue-400 font-semibold group"
                      whileHover={{ x: 5 }}
                    >
                      Learn More 
                      <motion.span
                        className="ml-2 transform-gpu"
                        animate={{
                          x: [0, 5, 0]
                        }}
                        transition={{
                          duration: 1.5,
                          repeat: Infinity,
                          ease: "linear"
                        }}
                      >
                        →
                      </motion.span>
                    </motion.div>
                  </Link> */}
                </div>
              </motion.div>
            </motion.div>
          ))}
        </div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mt-32"
        >
          <Link to="/contact">
            <motion.button
              className="px-12 py-6 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 text-white text-xl font-bold rounded-full relative overflow-hidden group shadow-[0_0_25px_rgba(191,219,254,0.3)] mb-4"
              whileHover={{ scale: 1.05, rotateX: 5 }}
              whileTap={{ scale: 0.95 }}
              style={{
                transformStyle: 'preserve-3d',
              }}
            >
              <motion.span
                className="absolute inset-0 bg-gradient-to-r from-white/30 to-transparent"
                animate={{
                  x: ['-200%', '200%']
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "linear"
                }}
              />
              Start Your Career Journey
            </motion.button>
          </Link>
        </motion.div>
      </div>
    </div>
  );
};

export default CareerSupport; 